/**
 * Free shipping by pieces: live progress bar + "complete your free shipping" picks.
 *
 * The theme adds/changes cart lines from many places (product page, quick add, quick view, drawer,
 * cart page, apps), so instead of hooking each one this watches every cart mutation request
 * (fetch + XHR to /cart/add|change|update|clear) and the theme's `cart:change` event, then re-reads
 * /cart.js once (debounced). Markup is server-rendered by snippets/free-shipping-progress.liquid.
 */
(function () {
  'use strict';

  var firstBar = document.querySelector('[data-fs-progress]');
  if (!firstBar) return;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var threshold = parseInt(firstBar.dataset.threshold, 10) || 2;
  var excluded = (firstBar.dataset.exclude || '').split(',').filter(Boolean);
  var msgs = {
    progress: firstBar.dataset.msgProgress || 'Add [count] more [piece] to unlock FREE shipping',
    again: firstBar.dataset.msgAgain || firstBar.dataset.msgProgress || '',
    unlocked: firstBar.dataset.msgUnlocked || "You've unlocked FREE shipping!"
  };
  var recsEnabled = firstBar.dataset.recs === 'true';
  var lastCount = parseInt(firstBar.dataset.count, 10) || 0;
  var wasUnlocked = lastCount >= threshold;
  var nativeFetch = window.fetch.bind(window);
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;

  function isCounted(item) {
    if (excluded.indexOf(item.handle) !== -1) return false;
    if (item.properties && item.properties._gift_wrap) return false;
    return true;
  }

  function countPieces(cart) {
    return (cart.items || []).reduce(function (sum, item) {
      return isCounted(item) ? sum + item.quantity : sum;
    }, 0);
  }

  function format(template, remaining) {
    return template
      .replace('[count]', remaining)
      .replace('[piece]', remaining === 1 ? 'piece' : 'pieces');
  }

  function formatMoney(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EGP';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' ' + currency;
    }
  }

  function track(name) {
    try {
      if (typeof window.clarity === 'function') {
        window.clarity('event', name);
        window.clarity('set', 'free_shipping_pieces', name);
      }
    } catch (e) {}
  }

  /* ---------- Progress bar ---------- */

  function render(cart) {
    var count = countPieces(cart);
    var remaining = Math.max(0, threshold - count);
    var unlocked = remaining === 0;
    var message = unlocked
      ? msgs.unlocked
      : format(wasUnlocked && count > 0 ? msgs.again : msgs.progress, remaining);

    document.querySelectorAll('[data-fs-progress]').forEach(function (bar) {
      bar.classList.toggle('is-unlocked', unlocked);
      bar.classList.toggle('is-empty', count === 0);
      var msgEl = bar.querySelector('[data-fs-msg]');
      if (msgEl && msgEl.textContent !== message) msgEl.textContent = message;
      bar.querySelectorAll('[data-fs-seg]').forEach(function (seg, i) {
        seg.classList.toggle('is-filled', i < count);
      });
      if (unlocked && lastCount < threshold) {
        bar.classList.remove('is-celebrating');
        void bar.offsetWidth; // restart the animation
        bar.classList.add('is-celebrating');
        setTimeout(function () { bar.classList.remove('is-celebrating'); }, 1800);
      }
    });

    if (unlocked && lastCount < threshold) track('free_shipping_unlocked');
    if (unlocked) wasUnlocked = true;
    lastCount = count;

    updateBadge(cart.item_count);
    updateRecs(cart, remaining);
  }

  // The header badge isn't updated by every add-to-cart path in the theme; keep it in sync here.
  function updateBadge(itemCount) {
    document.querySelectorAll('.cart-count, [data-cart-count]').forEach(function (el) {
      if (el.textContent !== String(itemCount)) el.textContent = itemCount;
      if (el.hasAttribute('data-cart-count')) el.setAttribute('data-cart-count', itemCount);
    });
  }

  var refreshTimer = null;
  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      nativeFetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) { if (cart) render(cart); })
        .catch(function () {});
    }, 150);
  }

  // Watch cart mutations made with fetch...
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var request = nativeFetch(input, init);
    if (CART_MUTATION.test(url)) {
      request.then(function (r) { if (r.ok) scheduleRefresh(); }).catch(function () {});
    }
    return request;
  };

  // ...and with XHR (some apps still use it)
  var xhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__fsCartMutation = CART_MUTATION.test(String(url || ''));
    return xhrOpen.apply(this, arguments);
  };
  var xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__fsCartMutation) {
      this.addEventListener('load', function () { if (this.status < 400) scheduleRefresh(); });
    }
    return xhrSend.apply(this, arguments);
  };

  // The theme's Cart class already passes fresh cart data with this event
  document.addEventListener('cart:change', function (e) {
    if (e.detail && e.detail.cart && e.detail.cart.items) render(e.detail.cart);
    else scheduleRefresh();
  });

  // Back/forward cache restores a stale page
  window.addEventListener('pageshow', function (e) { if (e.persisted) scheduleRefresh(); });

  /* ---------- Complete-your-free-shipping picks (drawer) ---------- */

  var recsBox = document.querySelector('[data-fs-recs]');
  var recsList = recsBox && recsBox.querySelector('[data-fs-recs-list]');
  var recsAnchor = null;
  var recsProducts = [];

  function updateRecs(cart, remaining) {
    if (!recsBox || !recsList) return;
    var counted = (cart.items || []).filter(isCounted);
    if (!recsEnabled || remaining === 0 || counted.length === 0) {
      recsBox.hidden = true;
      return;
    }
    var inCart = cart.items.map(function (i) { return i.product_id; });
    var anchor = counted[0].product_id; // most recently added line comes first

    if (anchor === recsAnchor) {
      drawRecs(inCart);
      return;
    }
    recsAnchor = anchor;
    nativeFetch(root + 'recommendations/products.json?product_id=' + anchor + '&limit=8&intent=related')
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .then(function (data) {
        if (recsAnchor !== anchor) return; // a newer request superseded this one
        recsProducts = (data.products || []).filter(function (p) {
          return p.available && excluded.indexOf(p.handle) === -1;
        });
        drawRecs(inCart);
      })
      .catch(function () { recsBox.hidden = true; });
  }

  function drawRecs(inCart) {
    var picks = recsProducts.filter(function (p) { return inCart.indexOf(p.id) === -1; }).slice(0, 3);
    recsList.textContent = '';
    if (!picks.length) {
      recsBox.hidden = true;
      return;
    }
    picks.forEach(function (p) { recsList.appendChild(buildCard(p)); });
    recsBox.hidden = false;
  }

  function optionIndex(product, pattern) {
    var opts = product.options || [];
    for (var i = 0; i < opts.length; i++) {
      var name = typeof opts[i] === 'string' ? opts[i] : opts[i].name;
      if (pattern.test(name)) return i;
    }
    return -1;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function sizedImage(src, width) {
    if (!src) return '';
    var url = typeof src === 'string' ? src : src.src;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function buildCard(product) {
    var colorIdx = optionIndex(product, /colou?r/i);
    var sizeIdx = optionIndex(product, /size/i);
    var firstAvailable = product.variants.filter(function (v) { return v.available; })[0] || product.variants[0];
    var state = { color: colorIdx > -1 ? firstAvailable.options[colorIdx] : null };

    var card = el('div', 'mz-fsr__item');
    var imgLink = el('a', 'mz-fsr__img');
    imgLink.href = product.url;
    var img = el('img');
    img.alt = product.title;
    img.width = 72;
    img.height = 90;
    img.loading = 'lazy';
    img.src = sizedImage(firstAvailable.featured_image || product.featured_image, 160);
    imgLink.appendChild(img);

    var info = el('div', 'mz-fsr__info');
    var title = el('a', 'mz-fsr__name', product.title);
    title.href = product.url;
    var price = el('div', 'mz-fsr__price', formatMoney(product.price));
    var choices = el('div', 'mz-fsr__choices');
    var status = el('p', 'mz-fsr__status');
    status.setAttribute('role', 'status');
    info.append(title, price, choices, status);
    card.append(imgLink, info);

    function variantsForColor() {
      return product.variants.filter(function (v) {
        return colorIdx === -1 || v.options[colorIdx] === state.color;
      });
    }

    function drawChoices() {
      choices.textContent = '';

      if (colorIdx > -1) {
        var colors = el('div', 'mz-fsr__chips mz-fsr__chips--color');
        colors.setAttribute('role', 'group');
        colors.setAttribute('aria-label', 'Color');
        (product.options[colorIdx].values || []).forEach(function (value) {
          var hasStock = product.variants.some(function (v) { return v.options[colorIdx] === value && v.available; });
          if (!hasStock) return;
          var chip = el('button', 'mz-fsr__chip mz-fsr__chip--color' + (value === state.color ? ' is-active' : ''), value);
          chip.type = 'button';
          chip.setAttribute('aria-pressed', value === state.color ? 'true' : 'false');
          chip.addEventListener('click', function () {
            state.color = value;
            var v = variantsForColor().filter(function (x) { return x.available; })[0];
            if (v && v.featured_image) img.src = sizedImage(v.featured_image, 160);
            drawChoices();
          });
          colors.appendChild(chip);
        });
        choices.appendChild(colors);
      }

      var sizes = el('div', 'mz-fsr__chips');
      if (sizeIdx > -1) {
        sizes.setAttribute('role', 'group');
        sizes.setAttribute('aria-label', 'Add size to cart');
        variantsForColor().forEach(function (v) {
          var chip = el('button', 'mz-fsr__chip', v.options[sizeIdx]);
          chip.type = 'button';
          chip.disabled = !v.available;
          chip.setAttribute('aria-label', 'Add ' + product.title + ' ' + (state.color ? state.color + ' ' : '') + 'size ' + v.options[sizeIdx] + ' to cart');
          chip.addEventListener('click', function () { addToCart(v.id, chip, status); });
          sizes.appendChild(chip);
        });
      } else {
        var add = el('button', 'mz-fsr__chip mz-fsr__chip--add', '+ Add');
        add.type = 'button';
        var v = variantsForColor().filter(function (x) { return x.available; })[0];
        add.disabled = !v;
        add.addEventListener('click', function () { if (v) addToCart(v.id, add, status); });
        sizes.appendChild(add);
      }
      choices.appendChild(sizes);
    }

    drawChoices();
    return card;
  }

  function addToCart(variantId, button, status) {
    button.classList.add('is-loading');
    button.disabled = true;
    status.textContent = '';
    window.fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (r) {
        return r.json().then(function (body) {
          if (!r.ok) throw new Error(body.description || body.message || 'Could not add this item');
        });
      })
      .then(function () {
        // Theme helper returns the cart enriched with variant data, so redrawn lines keep their variant picker
        if (window.cart && typeof window.cart.createCartPromise === 'function') return window.cart.createCartPromise();
        return nativeFetch(root + 'cart.js').then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        track('free_shipping_pick_added');
        // Let the theme redraw the drawer lines, totals and badge (it also fires cart:change)
        if (window.cart && typeof window.cart.updateCartState === 'function') {
          window.cart.updateCartState(cart);
        } else {
          render(cart);
        }
      })
      .catch(function (err) {
        status.textContent = err.message;
        button.disabled = false;
      })
      .finally(function () { button.classList.remove('is-loading'); });
  }

  // Picks for a cart that already has items when the page loads
  if (recsBox && recsEnabled && lastCount > 0 && lastCount < threshold) scheduleRefresh();
})();
